import { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSButton,
    NSCard,
    NSInput,
    NSLabel, NSSelect,
} from 'bricks';
import { format, parseISO } from 'date-fns';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './ProFormaPermanentDebt.scss';
import RenderIf from 'components/RenderIf/RenderIf';
import { IPatchPermanentDebt, PermanentDebtSizingMethod } from 'views/ProFormaTable/types';
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { IOptionAnyValue } from 'bricks/types';
import { CALCULATION_METHOD_OPTIONS } from './constants';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import DebtServiceTable from './DebtServiceTable/DebtServiceTable';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import BaseTotalsTable from '../BaseTotalsTable/BaseTotalsTable';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';
import { getDebtFundingWindow } from '../OperatingInformation/helpers';
import { useTypedFlags } from 'hooks/useTypedFlags';
import AdditionalLoan from './AdditionalLoan/AdditionalLoan';
import PermDebtSummary from './PermDebtSummary/PermDebtSummary';
import PermanentDebtInputSteppers from './PermanentDebtInputSteppers/PermanentDebtInputSteppers';
import { IconPlus } from '@tabler/icons-react';

export interface IProFormaPermanentDebtProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaPermanentDebt = ({
    operationBeginIndex, baseCashFlowColumns,
}: IProFormaPermanentDebtProps) => {
    const {
        operatingInfo, permanentDebtsInfo, permanentDebtsAggregate,
        handleUpdatePermanentDebt, handleCreatePermanentDebt, handleDeletePermanentDebt, showCents,
        periodCadence, cashFlowPeriods,
        sources, sourceTypes,
    } = useProFormaSocketContext();

    const { ldFlagMultiPermanentDebt } = useTypedFlags();

    const seniorPermanentLoan = permanentDebtsInfo?.find(debt => debt.isSenior)
        ?? permanentDebtsInfo?.[0]
        ?? ({} as typeof permanentDebtsInfo[number]);

    const additionalLoans = (permanentDebtsInfo ?? [])
        .filter(debt => !debt.isSenior)
        .sort((a, b) => {
            const priorityDiff = (a.payoffPriority ?? Infinity) - (b.payoffPriority ?? Infinity);
            if (priorityDiff !== 0) return priorityDiff;
            return (a.name ?? '').localeCompare(b.name ?? '');
        });

    const [loanName, setLoanName] = useState(seniorPermanentLoan?.name || '');

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('permanent-debt-table', ['income-table', 'expenses-table']);

    const updatePermanentDebtKeys = (keyValues: Partial<IPatchPermanentDebt>) => {
        if (operatingInfo?.id && seniorPermanentLoan?.id) {
            handleUpdatePermanentDebt(operatingInfo.id, seniorPermanentLoan.id, keyValues);
        }
    };

    let cashFlowAfterFinancingPeriods = seniorPermanentLoan?.cashFlowAfterFinancing?.cashFlow || [];
    if (periodCadence === Periods.ANNUALLY) cashFlowAfterFinancingPeriods = seniorPermanentLoan?.cashFlowAfterFinancing?.yearlyCashFlow || [];
    const cashFlowAfterFinancingValues = cashFlowAfterFinancingPeriods.map(period => period.value);

    let aggregateTotalDebtServicePeriods = permanentDebtsAggregate?.totalDebtService?.cashFlow || [];
    if (periodCadence === Periods.ANNUALLY) {
        aggregateTotalDebtServicePeriods = permanentDebtsAggregate?.totalDebtService?.yearlyCashFlow || [];
    }
    const aggregateTotalDebtServiceValues = aggregateTotalDebtServicePeriods.map(period => period.value);

    let aggregateCashFlowAfterFinancingPeriods = permanentDebtsAggregate?.cashFlowAfterFinancing?.cashFlow || [];
    if (periodCadence === Periods.ANNUALLY) {
        aggregateCashFlowAfterFinancingPeriods = permanentDebtsAggregate?.cashFlowAfterFinancing?.yearlyCashFlow || [];
    }
    const aggregateCashFlowAfterFinancingValues = aggregateCashFlowAfterFinancingPeriods.map(period => period.value);

    const { lastDebtMonth } = getDebtFundingWindow(sources, sourceTypes);
    const loanFundingFloor = Math.max(
        (lastDebtMonth ?? -1) + 1,
        operatingInfo.stabilizedMonth ?? 0,
    );
    const loanFundingPeriods = cashFlowPeriods.slice(loanFundingFloor);

    const loanFundingDatePlaceholder = `${seniorPermanentLoan?.loanFundingDate
        ? `${format(parseISO(seniorPermanentLoan?.loanFundingDate), 'MMM yyyy')} (Month ${seniorPermanentLoan.loanFundingMonth})` : ''}`;

    const loanFundingDateOptions = loanFundingPeriods.map((periodDate, index) => ({
        label: `${format(parseISO(periodDate), 'MMM yyyy')} (Month ${loanFundingFloor + index})`,
        value: periodDate,
    }));

    const handleLoanFundingOptionSelect = (option: IOptionAnyValue) => {
        if (operatingInfo?.id && seniorPermanentLoan?.id) {
            handleUpdatePermanentDebt(operatingInfo.id, seniorPermanentLoan.id, { loanFundingDate: option.value });
        }
    };

    const handleLoanNameBlur = () => {
        if (loanName !== (seniorPermanentLoan?.name || '')) {
            updatePermanentDebtKeys({ name: loanName });
        }
    };

    useEffect(() => {
        setLoanName(seniorPermanentLoan?.name || '');
    }, [seniorPermanentLoan?.name]);

    return (
        <div className="ProFormaPermanentDebt">
            <Row className="mx-n1 pb-3 px-3">
                <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                    <p className="mt-3 mb-0 font-16">Senior loan</p>
                    <hr className="mt-1 w-100" />
                </RenderIf>
                <Col className="px-1 pb-2" sm={12} md={6}>
                    <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className="pl-0 col-5 my-auto font-weight-normal text-dark text-nowrap">Loan name</NSLabel>
                            <div className="ProFormaPermanentDebt__input-width">
                                <NSInput
                                    name="loanName"
                                    type="text"
                                    value={loanName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLoanName(e.target.value)}
                                    onBlur={handleLoanNameBlur}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === 'Enter') e.currentTarget.blur();
                                    }}
                                    placeholder="Default"
                                />
                            </div>
                        </div>
                    </RenderIf>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 my-auto font-weight-normal text-dark text-nowrap">Loan funding</NSLabel>
                        <NSSelect
                            name="loanFundingDate"
                            options={loanFundingDateOptions}
                            value={loanFundingDateOptions.find(option => {
                                if (!seniorPermanentLoan?.loanFundingDate) return false;
                                const optDate = parseISO(option.value);
                                const infoDate = parseISO(seniorPermanentLoan.loanFundingDate);
                                return optDate.getFullYear() === infoDate.getFullYear()
                                    && optDate.getMonth() === infoDate.getMonth();
                            })}
                            onChange={handleLoanFundingOptionSelect}
                            placeholder={loanFundingDatePlaceholder}
                            isClearable={false}
                            isSearchable
                            menuPortalTarget={document.body}
                            formClassName="ProFormaPermanentDebt__select-width"
                        />
                    </div>
                    <PermanentDebtInputSteppers
                        permanentDebtInfo={seniorPermanentLoan}
                        saleMonth={operatingInfo.saleMonth ?? 0}
                        onUpdate={updatePermanentDebtKeys}
                    />
                    <hr className="mt-2 my-1 ProFormaPermanentDebt__form-width" />
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 font-weight-normal text-dark text-nowrap">
                            Calculation method
                        </NSLabel>
                        <NSSelect
                            options={CALCULATION_METHOD_OPTIONS}
                            name="permanentDebtSizingMethod"
                            value={CALCULATION_METHOD_OPTIONS.find(
                                option => (seniorPermanentLoan.permanentDebtSizingMethod || 'default') === option.value
                            )}
                            onChange={option => updatePermanentDebtKeys({ permanentDebtSizingMethod: option.value })}
                            isClearable={false}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            formClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>

                    <RenderIf isTrue={seniorPermanentLoan.permanentDebtSizingMethod === PermanentDebtSizingMethod.dscr}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target DSCR
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetDscr"
                                    value={seniorPermanentLoan.targetDscr != null || seniorPermanentLoan.impliedDscr != null ?
                                        Number((seniorPermanentLoan.targetDscr ?? seniorPermanentLoan.impliedDscr ?? 0).toFixed(2))
                                        : null}
                                    onChange={(value: number) => updatePermanentDebtKeys({ targetDscr: value })}
                                    min={0}
                                    step={0.05}
                                    customSuffix="x"
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>

                    <RenderIf isTrue={seniorPermanentLoan.permanentDebtSizingMethod === PermanentDebtSizingMethod.debt_yield}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target debt yield
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetDebtYield"
                                    value={seniorPermanentLoan.targetDebtYield ?? seniorPermanentLoan.impliedDebtYield ?? null}
                                    onChange={(value: number) => updatePermanentDebtKeys({ targetDebtYield: value })}
                                    min={0}
                                    step={0.25}
                                    max={100}
                                    percentage
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>

                    <RenderIf isTrue={seniorPermanentLoan.permanentDebtSizingMethod === PermanentDebtSizingMethod.ltv}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target LTV
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetLtv"
                                    value={seniorPermanentLoan.targetLtv ?? seniorPermanentLoan.impliedLtv ?? null}
                                    onChange={(value: number) => updatePermanentDebtKeys({ targetLtv: value })}
                                    min={0}
                                    step={1}
                                    percentage
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>
                </Col>
                <Col className="px-1" sm={12} md={6}>
                    <Row className="mx-n1 pt-2">
                        <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                            <Col className="px-1 mb-2" sm={12} md={6}>
                                <MetricOutputCard
                                    label="Senior loan amount"
                                    value={numeralFormatterCurrency(
                                        Math.abs(seniorPermanentLoan.loanFundingAmount || 0),
                                        showCents ? '$0,0.00' : '$0,0',
                                    )}
                                />
                            </Col>
                        </RenderIf>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Fees & closing costs"
                                value={numeralFormatterCurrency(
                                    Math.abs(seniorPermanentLoan.feesAndClosingCostsAmount || 0),
                                    showCents ? '$0,0.00' : '$0,0',
                                )}
                            />
                        </Col>
                        <RenderIf isTrue={!ldFlagMultiPermanentDebt}>
                            <Col className="px-1 mb-2" sm={12} md={6}>
                                <MetricOutputCard
                                    label="Loan payoff"
                                    value={`${seniorPermanentLoan.loanPayoffDate
                                        ? `${format(parseISO(seniorPermanentLoan.loanPayoffDate), 'MMM yyyy')} `
                                        + `(Month ${seniorPermanentLoan.loanPayoffMonth})` : null}`}
                                    showInProgress={!seniorPermanentLoan.loanPayoffDate}
                                />
                            </Col>
                        </RenderIf>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Debt service coverage ratio"
                                value={`${numeralFormatterCurrency(seniorPermanentLoan.debtServiceCoverageRatio || 0, '0,0.00')}x`}
                                showInProgress={!seniorPermanentLoan.debtServiceCoverageRatio}
                            />
                        </Col>
                        <RenderIf isTrue={!ldFlagMultiPermanentDebt}>
                            <Col className="px-1 mb-2" sm={12} md={6}>
                                <MetricOutputCard
                                    label="Cash-on-cash return"
                                    value={`${percentFormatter(
                                        (seniorPermanentLoan.cashOnCashReturn || 0) * 100,
                                    )} (Month ${seniorPermanentLoan.cashOnCashReturnMonth})`}
                                    showInProgress={!seniorPermanentLoan.cashOnCashReturn}
                                />
                            </Col>
                            <Col className="px-1 mb-2" sm={12} md={6}>
                                <MetricOutputCard
                                    label="Net equity at refinance"
                                    value={numeralFormatterCurrency(seniorPermanentLoan.equityDifference || 0, showCents ? '$0,0.00' : '$0,0')}
                                    showInProgress={seniorPermanentLoan.equityDifference === null}
                                />
                            </Col>
                        </RenderIf>
                        <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                            <Col className="px-1 mb-2" sm={12} md={6}>
                                <MetricOutputCard
                                    label="Annual debt service"
                                    value={numeralFormatterCurrency(seniorPermanentLoan.debtService?.annualPayment || 0, showCents ? '$0,0.00' : '$0,0')}
                                />
                            </Col>
                        </RenderIf>
                    </Row>
                </Col>
            </Row>
            <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                <Row className="mx-n1 pb-3 px-3">
                    <p className="mt-3 mb-0 font-16">Additional loans</p>
                    <hr className="mt-1 w-100" />
                    {additionalLoans.map((loanData, index) => {
                        const priorityOptions = additionalLoans.map((debt, idx) => ({
                            label: `#${idx + 2}`,
                            value: idx + 2,
                        }));

                        return (
                            <AdditionalLoan
                                key={`additional-loan-${loanData.id ?? index}`}
                                loanData={loanData}
                                loanNumber={index + 2}
                                saleMonth={operatingInfo.saleMonth ?? 0}
                                showCents={showCents}
                                priorityOptions={priorityOptions}
                                onUpdate={(keyValues) => {
                                    if (operatingInfo?.id && loanData.id) {
                                        handleUpdatePermanentDebt(
                                            operatingInfo.id,
                                            loanData.id,
                                            keyValues,
                                        );
                                    }
                                }}
                                onDelete={() => {
                                    if (operatingInfo?.id && loanData.id) {
                                        handleDeletePermanentDebt(operatingInfo.id, loanData.id);
                                    }
                                }}
                            />
                        );
                    })}
                    <div className="d-flex justify-content-end w-100">
                        <NSButton
                            text="Add loan"
                            callback={() => {
                                if (operatingInfo?.id) {
                                    handleCreatePermanentDebt(operatingInfo.id);
                                }
                            }}
                            size='sm'
                            ariaLabel="Add loan"
                            className='mt-2'
                            icon={IconPlus}
                            color='primary'
                            outline
                        />
                    </div>
                </Row>
            </RenderIf>
            <RenderIf isTrue={!ldFlagMultiPermanentDebt}>
                <div
                    className={classNames(['pb-3 pl-3', {
                        'pr-3': !operationBeginIndex,
                    }])}
                >
                    <div
                        className="ProFormaPermanentDebt__table-wrapper scrollable-container"
                        ref={createRefHandler(tableRef)}
                        onScroll={() => handleTableScroll('table')}
                    >
                        <DebtServiceTable
                            baseCashFlowColumns={baseCashFlowColumns}
                        />
                        <BaseTotalsTable
                            tableName="Cash flow after financing"
                            totalAmount={seniorPermanentLoan?.cashFlowAfterFinancing?.totalAnnualCashFlow || 0}
                            cashFlowValues={cashFlowAfterFinancingValues}
                            operationBeginIndex={operationBeginIndex}
                            baseCashFlowColumns={baseCashFlowColumns}
                        />
                    </div>
                </div>
            </RenderIf>
            <RenderIf isTrue={ldFlagMultiPermanentDebt}>
                <div
                    className={classNames(['pb-3 pl-3', {
                        'pr-3': !operationBeginIndex,
                    }])}
                >
                    <div
                        className="ProFormaPermanentDebt__table-wrapper scrollable-container"
                        ref={createRefHandler(tableRef)}
                        onScroll={() => handleTableScroll('table')}
                    >
                        <DebtServiceTable
                            baseCashFlowColumns={baseCashFlowColumns}
                        />
                        <BaseTotalsTable
                            tableName="Total debt service"
                            totalAmount={permanentDebtsAggregate?.totalDebtService?.annualPayment || 0}
                            cashFlowValues={aggregateTotalDebtServiceValues}
                            operationBeginIndex={operationBeginIndex}
                            baseCashFlowColumns={baseCashFlowColumns}
                        />
                        <BaseTotalsTable
                            tableName="Cash flow after financing"
                            totalAmount={permanentDebtsAggregate?.cashFlowAfterFinancing?.totalAnnualCashFlow || 0}
                            cashFlowValues={aggregateCashFlowAfterFinancingValues}
                            operationBeginIndex={operationBeginIndex}
                            baseCashFlowColumns={baseCashFlowColumns}
                        />
                    </div>
                </div>
                <PermDebtSummary
                    permanentDebtsAggregate={permanentDebtsAggregate}
                    seniorPermanentLoan={seniorPermanentLoan}
                    showCents={showCents}
                />
            </RenderIf>
        </div>
    );
};

export default ProFormaPermanentDebt;
